/**
 * OptimizerPage
 * Main page for the SVS (Slot Viability Score) Optimizer module.
 *
 * This page only runs the Part-5 optimizer over a real project's data
 * (?projectId=... in the URL). The synthetic-data demo mode has been
 * removed entirely — there is no path that solves invented flights.
 *
 * Features:
 * - Configuration panel (capacity limit, min turnaround, etc.), seeded from
 *   the project's DACAMS analysis_results where a real value exists
 * - Async "Run on project" (Celery job) with progress polling
 * - Canonical Part-5 KPIs, Tri-Node hourly compliance table, provenance
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { optimizerService } from '../services/optimizerService';
import { projectsService } from '../services/projects';
import InfoHint from '../components/common/InfoHint';
import type {
    OptimizerConfig,
    MIPOptimizerResult,
    OptimizerStatusResponse,
} from '../types/optimizer';
import { DEFAULT_OPTIMIZER_CONFIG, minutesToTime } from '../types/optimizer';
import { Settings, RefreshCw, XCircle, CheckCircle2, PuzzleIcon, Target } from 'lucide-react';
import {
    PlayIcon,
    ScalesIcon,
    ShieldCheckIcon,
    ShieldWarningIcon,
    WarningIcon,
    ChartLineUpIcon,
    CaretDownIcon,
    CaretRightIcon,
    ArrowRightIcon,
} from '@phosphor-icons/react';
import { useLanguage } from '../context/LanguageContext';

const TEXTS = {
    es: {
        title: 'Optimizer — SVS',
        subtitle: 'Slot Viability Score • Asignación de slots MIP en bandas horarias disponibles',
        config: 'Configuración',
        capacityLimit: 'Capacidad Límite (mov/hora)',
        minTurnaround: 'Turnaround Mínimo',
        lambdaWeight: 'Peso λ (slots vs slack)',
        maxSlots: 'Máximo Slots a Crear',
        timeWindow: 'Ventana Operativa',
        orReady: 'OR-Tools Listo',
        orNotAvailable: 'OR-Tools No Disponible',
        completed: 'Optimización Completada',
        slotsCreated: 'Slots Creados',
        minRecovered: 'Min. Recuperados',
        fragReduction: 'Reducción Frag.',
        solveTime: 'Tiempo (ms)',
        runningMip: 'Ejecutando MIP Solver (SCIP)...',
        consolidating: 'Consolidando slack → slots comercializables',
        emptyTitle: 'Sin resultados aún',
        emptyDesc: 'Ejecuta el optimizer sobre el proyecto para ver los slots creados y los KPIs canónicos.',
        narrow: '(narrow)',
        wide: '(wide)',
        minSlack: 'Min Slack',
        maxSlotsLabel: 'Max Slots',
        runProjectBtn: 'Ejecutar sobre el proyecto',
        noProjectMessage: 'Abre el Optimizer desde un proyecto para ejecutarlo sobre sus datos.',
        goToProjects: 'Ir a proyectos',
        projectMode: 'Modo proyecto real',
        projectRunning: 'Ejecutando sobre el proyecto...',
        projectQueued: 'En cola',
        projectProgress: 'Progreso',
        noAssignmentTitle: 'Sin asignación vuelo-stand',
        canonicalKpisTitle: 'KPIs canónicos (Parte 5)',
        equityIndex: 'Equidad (1−Gini)',
        fragReductionCanonical: 'Reducción de fragmentación',
        utilizationGain: 'Ganancia de utilización',
        violations: 'Violaciones',
        nodeComplianceKpi: 'Node-compliance',
        nodeComplianceOk: 'OK',
        nodeComplianceFail: 'Fallo',
        nodeComplianceTitle: 'Node-compliance por hora (tri-nodo)',
        nodeComplianceDesc: 'Los slots creados solo pueden usar el headroom disponible del tri-nodo (cap_cc − existing_mov). Las horas resaltadas violan ese límite.',
        colHour: 'Hora',
        colCapCc: 'Cap. controladora (CC)',
        colExistingMov: 'Mov. existentes',
        colHeadroom: 'Headroom',
        colSlotsUsed: 'Slots usados',
        provenanceTitle: 'Provenance (glass box)',
        provenanceEmpty: 'Sin datos de provenance para este resultado.',
        fromDacamsBadge: 'Desde DACAMS',
        defaultBadge: 'Por defecto',
        lambdaHint: "Lambda equilibra dos objetivos del optimizador: minimizar el 'slack' (capacidad desperdiciada entre operaciones) frente a crear el mayor número de 'slots' viables. Más hacia slots = agenda más densa; más hacia slack = más holgura.",
        capacityHint: 'Capacidad controladora (CC): el máximo de movimientos por hora que el sistema puede sostener, el mínimo del tri-nodo pista/plataforma/terminal. Viene del análisis DACAMS.',
        turnaroundHint: 'Tiempo mínimo de rotación: minutos que un avión ocupa su stand entre llegada y salida. Marca cuánto puede comprimirse la agenda.',
    },
    en: {
        title: 'Optimizer — SVS',
        subtitle: 'Slot Viability Score • MIP-based slot assignment in available hourly bands',
        config: 'Configuration',
        capacityLimit: 'Capacity Limit (mov/hour)',
        minTurnaround: 'Min. Turnaround',
        lambdaWeight: 'λ Weight (slots vs slack)',
        maxSlots: 'Max Slots to Create',
        timeWindow: 'Operating Window',
        orReady: 'OR-Tools Ready',
        orNotAvailable: 'OR-Tools Not Available',
        completed: 'Optimization Complete',
        slotsCreated: 'Slots Created',
        minRecovered: 'Min. Recovered',
        fragReduction: 'Frag. Reduction',
        solveTime: 'Time (ms)',
        runningMip: 'Running MIP Solver (SCIP)...',
        consolidating: 'Consolidating slack → marketable slots',
        emptyTitle: 'No results yet',
        emptyDesc: 'Run the optimizer over the project to see the created slots and canonical KPIs.',
        narrow: '(narrow)',
        wide: '(wide)',
        minSlack: 'Min Slack',
        maxSlotsLabel: 'Max Slots',
        runProjectBtn: 'Run on project',
        noProjectMessage: 'Open the Optimizer from a project to run it on that project\'s data.',
        goToProjects: 'Go to projects',
        projectMode: 'Real project mode',
        projectRunning: 'Running on project...',
        projectQueued: 'Queued',
        projectProgress: 'Progress',
        noAssignmentTitle: 'No flight-stand assignment',
        canonicalKpisTitle: 'Canonical KPIs (Part 5)',
        equityIndex: 'Equity (1−Gini)',
        fragReductionCanonical: 'Fragmentation reduction',
        utilizationGain: 'Utilization gain',
        violations: 'Violations',
        nodeComplianceKpi: 'Node-compliance',
        nodeComplianceOk: 'OK',
        nodeComplianceFail: 'Fail',
        nodeComplianceTitle: 'Hourly node-compliance (tri-node)',
        nodeComplianceDesc: 'Created slots may only use the tri-node headroom available (cap_cc − existing_mov). Highlighted hours violate that limit.',
        colHour: 'Hour',
        colCapCc: 'Controlling capacity (CC)',
        colExistingMov: 'Existing mov.',
        colHeadroom: 'Headroom',
        colSlotsUsed: 'Slots used',
        provenanceTitle: 'Provenance (glass box)',
        provenanceEmpty: 'No provenance data for this result.',
        fromDacamsBadge: 'From DACAMS',
        defaultBadge: 'Default',
        lambdaHint: "Lambda balances two optimizer objectives: minimizing 'slack' (wasted capacity between operations) versus creating the largest number of viable 'slots'. More toward slots = a denser schedule; more toward slack = more buffer.",
        capacityHint: 'Controlling Capacity (CC): the maximum movements per hour the system can sustain, the minimum of the runway/apron/terminal tri-node. Comes from the DACAMS analysis.',
        turnaroundHint: 'Minimum turnaround time: minutes an aircraft occupies its stand between arrival and departure. Sets how tightly the schedule can be compressed.',
    },
};

// Styles
const styles: Record<string, React.CSSProperties> = {
    container: {
        minHeight: '100vh',
        backgroundColor: 'var(--bg)',
        color: 'var(--text-primary)',
        padding: '24px',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px',
    },
    title: {
        fontSize: '28px',
        fontWeight: 700,
        background: 'linear-gradient(135deg, #F26430, #FF8C42)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        margin: 0,
    },
    subtitle: {
        fontSize: '14px',
        color: 'var(--text-secondary)',
        marginTop: '4px',
    },
    backButton: {
        padding: '10px 20px',
        backgroundColor: 'var(--surface-raised)',
        color: 'var(--text-primary)',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '14px',
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: '320px 1fr',
        gap: '24px',
    },
    panel: {
        backgroundColor: 'var(--surface)',
        borderRadius: '12px',
        padding: '24px',
        border: '1px solid var(--border)',
    },
    panelTitle: {
        fontSize: '16px',
        fontWeight: 600,
        marginBottom: '20px',
        color: 'var(--text-primary)',
    },
    formGroup: {
        marginBottom: '16px',
    },
    label: {
        display: 'block',
        fontSize: '12px',
        color: 'var(--text-secondary)',
        marginBottom: '6px',
        fontWeight: 500,
    },
    input: {
        width: '100%',
        padding: '10px 12px',
        backgroundColor: 'var(--surface-inset)',
        border: '1px solid var(--border)',
        borderRadius: '6px',
        color: 'var(--text-primary)',
        fontSize: '14px',
        outline: 'none',
    },
    slider: {
        width: '100%',
        height: '6px',
        borderRadius: '3px',
        outline: 'none',
        appearance: 'auto',
    },
    button: {
        width: '100%',
        padding: '14px',
        backgroundColor: '#F26430',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '15px',
        fontWeight: 600,
        cursor: 'pointer',
        marginTop: '20px',
        transition: 'all 0.2s ease',
    },
    buttonDisabled: {
        opacity: 0.5,
        cursor: 'not-allowed',
    },
    statusBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 12px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: 500,
    },
    resultCard: {
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        border: '1px solid rgba(16, 185, 129, 0.3)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '24px',
    },
    resultGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '16px',
        marginTop: '16px',
    },
    resultItem: {
        textAlign: 'center' as const,
    },
    resultValue: {
        fontSize: '24px',
        fontWeight: 700,
        color: 'var(--aipron-success)',
    },
    resultLabel: {
        fontSize: '11px',
        color: 'var(--text-secondary)',
        marginTop: '4px',
    },
    loadingOverlay: {
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px',
        color: 'var(--text-secondary)',
    },
    spinner: {
        width: '40px',
        height: '40px',
        border: '3px solid var(--border)',
        borderTopColor: '#F26430',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
    },
};

const OptimizerPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const projectId = searchParams.get('projectId');
    const { language } = useLanguage();
    const tx = TEXTS[(language as 'es' | 'en') || 'en'] || TEXTS.en;

    // State
    const [status, setStatus] = useState<OptimizerStatusResponse | null>(null);
    const [config, setConfig] = useState<OptimizerConfig>(DEFAULT_OPTIMIZER_CONFIG);
    const [result, setResult] = useState<MIPOptimizerResult | null>(null);

    // Real-project run state (Fase 4d)
    const [projectLoading, setProjectLoading] = useState(false);
    const [projectProgress, setProjectProgress] = useState<number | null>(null);
    const [projectError, setProjectError] = useState<string | null>(null);
    const [projectNoAssignment, setProjectNoAssignment] = useState(false);
    const [provenanceOpen, setProvenanceOpen] = useState(false);
    // Canonical to-be (FTS bridge decision #2): computes the to-be tri-node via
    // the datasheet -> one DACAMS formula, persists tobe_controlling_capacity.
    const [tobeLoading, setTobeLoading] = useState(false);
    const [tobeProgress, setTobeProgress] = useState<number | null>(null);
    const [tobeError, setTobeError] = useState<string | null>(null);
    const [tobeResult, setTobeResult] = useState<{
        available?: boolean; asis_cc?: number | null; tobe_cc?: number | null;
        deltas?: { rac?: number | null; cc?: number | null } | null;
    } | null>(null);
    // Size the optimizer against the to-be CC envelope instead of the as-is CC.
    const [useTobeCapacity, setUseTobeCapacity] = useState(false);
    const pollTimerRef = useRef<number | null>(null);

    // Track which config fields were seeded from the project's DACAMS
    // analysis_results (vs. left at their hardcoded default). Same
    // dacamsOrigins-set pattern as RunCapPage — never label a field "from
    // DACAMS" unless a real value was actually read.
    const [dacamsOrigins, setDacamsOrigins] = useState<Set<string>>(new Set());

    useEffect(() => {
        return () => {
            if (pollTimerRef.current !== null) {
                window.clearTimeout(pollTimerRef.current);
            }
        };
    }, []);

    // Seed optimizer config defaults from the project's DACAMS analysis_results.
    // Only fields whose real key was actually found get overwritten and marked
    // as "from DACAMS" — everything else keeps the hardcoded default from
    // DEFAULT_OPTIMIZER_CONFIG, labeled as a default (REGLA DE ORO).
    useEffect(() => {
        if (!projectId) return;
        let cancelled = false;

        (async () => {
            try {
                const proj = await projectsService.getOne(projectId);
                if (!proj || cancelled) return;

                const ar = (proj as any).analysis_results || {};
                const parts = ar.parts || {};
                const loaded: string[] = [];
                const updates: Partial<OptimizerConfig> = {};

                // capacity_limit <- Tri-Node controlling capacity (Part 3 equilibrium).
                // Same value is mirrored at kpis.controlling_capacity; either is a
                // verified, real key — prefer the canonical parts.part3_equilibrium path.
                const cc = parts.part3_equilibrium?.controlling_capacity
                    ?? ar.kpis?.controlling_capacity;
                if (typeof cc === 'number' && cc > 0) {
                    updates.capacity_limit = cc;
                    loaded.push('capacity_limit');
                }

                // min_turnaround <- median actual turnaround from AODB operational KPIs
                // (analysis_results.kpis.aodb_operational_kpis.airport_global.median_tat_min,
                // mirrored at parts.aodb_operational_kpis.airport_global). Fall back to the
                // P50 or the mean only when the median itself is absent.
                const airportGlobal = ar.kpis?.aodb_operational_kpis?.airport_global
                    ?? parts.aodb_operational_kpis?.airport_global;
                const tat = airportGlobal?.median_tat_min
                    ?? airportGlobal?.p50_tat_min
                    ?? airportGlobal?.avg_tat_min;
                if (typeof tat === 'number' && tat > 0) {
                    updates.min_turnaround = Math.round(tat);
                    loaded.push('min_turnaround');
                }

                // day_start / day_end: no verified operating-window key exists in
                // analysis_results today — keep the hardcoded defaults (labeled as
                // defaults; nothing added to `loaded` for them).

                if (cancelled) return;
                if (Object.keys(updates).length > 0) {
                    setConfig(prev => ({ ...prev, ...updates }));
                }
                setDacamsOrigins(new Set(loaded));
            } catch (err) {
                console.error('Failed to load project for optimizer seeding:', err);
            }
        })();

        return () => { cancelled = true; };
    }, [projectId]);

    // Poll a Celery optimizer job until it reaches a terminal state.
    const pollProjectTask = (taskId: string) => {
        const poll = async () => {
            try {
                const task = await optimizerService.getProjectTask(taskId);
                if (task.state === 'SUCCESS') {
                    setProjectLoading(false);
                    setProjectProgress(null);
                    // The job completes even when there was nothing to optimise: the
                    // task returns NO_ALLOCATION rather than solving invented flights.
                    // Surface that as the honest "no data" state, not an empty result.
                    if (task.result?.status === 'NO_ALLOCATION') {
                        setProjectNoAssignment(true);
                        setProjectError(task.result.error || null);
                    } else if (task.result?.status === 'ERROR') {
                        setProjectError(task.result.error || 'Optimization failed');
                    } else if (task.result) {
                        setResult(task.result);
                    }
                } else if (task.state === 'FAILURE') {
                    setProjectLoading(false);
                    setProjectProgress(null);
                    setProjectError(task.error || 'Optimization failed');
                } else {
                    // PENDING / PROGRESS / any other non-terminal state
                    setProjectProgress(typeof task.progress === 'number' ? task.progress : null);
                    pollTimerRef.current = window.setTimeout(poll, 2000);
                }
            } catch (err: any) {
                setProjectLoading(false);
                setProjectProgress(null);
                setProjectError(err?.message || 'Failed to poll optimizer task');
            }
        };
        poll();
    };

    // Run the Part-5 optimizer over the real project (async Celery job).
    const runOnProject = async () => {
        if (!projectId) return;
        setProjectLoading(true);
        setProjectProgress(null);
        setProjectError(null);
        setProjectNoAssignment(false);

        try {
            const { task_id } = await optimizerService.runForProjectAsync(projectId, {
                capacity_limit: config.capacity_limit,
                min_turnaround: config.min_turnaround,
                lambda_weight: config.lambda_weight,
                max_slots: config.max_slots,
                day_start: config.day_start,
                day_end: config.day_end,
                time_limit_seconds: config.time_limit_seconds,
                // Size slots against the to-be CC envelope when requested (reads the
                // tobe_controlling_capacity persisted by the canonical to-be run).
                capacity_source: useTobeCapacity ? 'to_be' : 'as_is',
            });
            pollProjectTask(task_id);
        } catch (err: any) {
            setProjectLoading(false);
            const message: string = err?.message || 'Optimizer enqueue failed';
            // 409 = the project has no per-flight stand allocation to optimise.
            // Detected by HTTP status (the service now attaches it), not by the
            // message text, so re-wording the backend string can't misroute it to
            // the generic error box. The backend detail is already explanatory.
            if (err?.status === 409) {
                setProjectNoAssignment(true);
            }
            setProjectError(message);
        }
    };

    // Compute the canonical to-be (background job) and surface as-is vs to-be CC.
    const pollTobeTask = (taskId: string) => {
        const poll = async () => {
            try {
                const task = await optimizerService.getProjectTask(taskId);
                if (task.state === 'SUCCESS') {
                    setTobeLoading(false);
                    setTobeProgress(null);
                    const r: any = task.result;
                    if (r?.status === 'ERROR') {
                        setTobeError(r.error || 'To-be computation failed');
                    } else if (r) {
                        setTobeResult({ available: r.available, asis_cc: r.asis_cc, tobe_cc: r.tobe_cc, deltas: r.deltas });
                    }
                } else if (task.state === 'FAILURE') {
                    setTobeLoading(false);
                    setTobeProgress(null);
                    setTobeError(task.error || 'To-be computation failed');
                } else {
                    setTobeProgress(typeof task.progress === 'number' ? task.progress : null);
                    pollTimerRef.current = window.setTimeout(poll, 2500);
                }
            } catch (err: any) {
                setTobeLoading(false);
                setTobeProgress(null);
                setTobeError(err?.message || 'Failed to poll to-be task');
            }
        };
        poll();
    };

    const computeTobe = async () => {
        if (!projectId) return;
        setTobeLoading(true);
        setTobeProgress(null);
        setTobeError(null);
        setTobeResult(null);
        try {
            const { task_id } = await optimizerService.computeCanonicalTobeAsync(projectId, { closure_factor: 0.5 });
            pollTobeTask(task_id);
        } catch (err: any) {
            setTobeLoading(false);
            setTobeError(err?.message || 'To-be enqueue failed');
        }
    };

    // Check optimizer status on mount
    useEffect(() => {
        async function checkStatus() {
            try {
                const s = await optimizerService.getStatus();
                setStatus(s);
            } catch (err) {
                console.error('Failed to get optimizer status:', err);
            }
        }
        checkStatus();
    }, []);

    const statusColor = status?.status === 'available' ? '#10B981' : '#EF4444';

    // No project loaded — there is no synthetic/demo path anymore, so there is
    // nothing meaningful to configure or run. Show an honest empty state that
    // sends the user to pick a project instead of a non-functional page.
    if (!projectId) {
        return (
            <div style={styles.container}>
                <div style={styles.header}>
                    <div>
                        <h1 style={styles.title} className="flex items-center gap-2"><PuzzleIcon className="inline w-7 h-7" /> {tx.title}</h1>
                        <p style={styles.subtitle}>
                            {tx.subtitle}
                        </p>
                    </div>
                </div>
                <div
                    style={{
                        ...styles.panel,
                        maxWidth: '480px',
                        margin: '0 auto',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        padding: '48px 32px',
                        gap: '16px',
                    }}
                >
                    <PuzzleIcon style={{ width: '40px', height: '40px', color: 'var(--text-tertiary)' }} />
                    <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>
                        {tx.noProjectMessage}
                    </p>
                    <button
                        onClick={() => navigate('/analysis')}
                        style={{
                            ...styles.button,
                            width: 'auto',
                            marginTop: '4px',
                            padding: '10px 24px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                        }}
                    >
                        {tx.goToProjects} <ArrowRightIcon size={16} />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            {/* Add keyframes for spinner */}
            <style>{`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}</style>

            {/* Header */}
            <div style={styles.header}>
                <div>
                    <h1 style={styles.title} className="flex items-center gap-2"><PuzzleIcon className="inline w-7 h-7" /> {tx.title}</h1>
                    <p style={styles.subtitle}>
                        {tx.subtitle}
                    </p>
                </div>
            </div>

            {/* Main Grid */}
            <div style={styles.grid}>
                {/* Configuration Panel */}
                <div style={styles.panel}>
                    <h3 style={styles.panelTitle} className="flex items-center gap-2"><Settings className="w-5 h-5" /> {tx.config}</h3>

                    {/* Status Badge */}
                    <div style={{
                        ...styles.statusBadge,
                        backgroundColor: `${statusColor}20`,
                        color: statusColor,
                        marginBottom: '20px',
                    }}>
                        <span style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: statusColor,
                        }} />
                        {status?.status === 'available' ? tx.orReady : tx.orNotAvailable}
                    </div>

                    {/* Capacity Limit */}
                    <div style={styles.formGroup}>
                        <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                            {tx.capacityLimit}
                            <InfoHint text={tx.capacityHint} />
                            <SourceBadge fromDacams={dacamsOrigins.has('capacity_limit')} tx={tx} />
                        </label>
                        <input
                            type="number"
                            style={styles.input}
                            value={config.capacity_limit}
                            onChange={e => setConfig({
                                ...config,
                                capacity_limit: parseFloat(e.target.value) || 30
                            })}
                            min={1}
                            max={100}
                        />
                    </div>

                    {/* Min Turnaround */}
                    <div style={styles.formGroup}>
                        <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                            {tx.minTurnaround}: {config.min_turnaround} min
                            <InfoHint text={tx.turnaroundHint} />
                            <SourceBadge fromDacams={dacamsOrigins.has('min_turnaround')} tx={tx} />
                        </label>
                        <input
                            type="range"
                            style={styles.slider}
                            value={config.min_turnaround}
                            onChange={e => setConfig({
                                ...config,
                                min_turnaround: parseInt(e.target.value)
                            })}
                            min={15}
                            max={120}
                            step={5}
                        />
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'var(--text-tertiary)' }}>
                            <span>15m</span>
                            <span>45m {tx.narrow}</span>
                            <span>90m {tx.wide}</span>
                            <span>120m</span>
                        </div>
                    </div>

                    {/* Lambda Weight */}
                    <div style={styles.formGroup}>
                        <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                            {tx.lambdaWeight}: {config.lambda_weight.toFixed(2)}
                            <InfoHint text={tx.lambdaHint} />
                        </label>
                        <input
                            type="range"
                            style={styles.slider}
                            value={config.lambda_weight * 100}
                            onChange={e => setConfig({
                                ...config,
                                lambda_weight: parseInt(e.target.value) / 100
                            })}
                            min={0}
                            max={100}
                        />
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'var(--text-tertiary)' }}>
                            <span>{tx.minSlack}</span>
                            <span>{tx.maxSlotsLabel}</span>
                        </div>
                    </div>

                    {/* Max Slots */}
                    <div style={styles.formGroup}>
                        <label style={styles.label}>
                            {tx.maxSlots}
                        </label>
                        <input
                            type="number"
                            style={styles.input}
                            value={config.max_slots}
                            onChange={e => setConfig({
                                ...config,
                                max_slots: parseInt(e.target.value) || 20
                            })}
                            min={1}
                            max={50}
                        />
                    </div>

                    {/* Time Window */}
                    <div style={styles.formGroup}>
                        <label style={styles.label}>
                            {tx.timeWindow}
                        </label>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <input
                                type="text"
                                style={{ ...styles.input, flex: 1 }}
                                value={minutesToTime(config.day_start)}
                                onChange={e => {
                                    const [h, m] = e.target.value.split(':').map(Number);
                                    if (!isNaN(h) && !isNaN(m)) {
                                        setConfig({ ...config, day_start: h * 60 + m });
                                    }
                                }}
                                placeholder="06:00"
                            />
                            <span style={{ alignSelf: 'center', color: 'var(--text-tertiary)' }}>a</span>
                            <input
                                type="text"
                                style={{ ...styles.input, flex: 1 }}
                                value={minutesToTime(config.day_end)}
                                onChange={e => {
                                    const [h, m] = e.target.value.split(':').map(Number);
                                    if (!isNaN(h) && !isNaN(m)) {
                                        setConfig({ ...config, day_end: h * 60 + m });
                                    }
                                }}
                                placeholder="23:00"
                            />
                        </div>
                    </div>

                    {/* Run on Real Project — the only run path (demo/synthetic mode removed) */}
                    <div style={{ marginTop: '20px' }}>
                        <div style={{
                            fontSize: '11px',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            color: 'var(--text-tertiary)',
                            marginBottom: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                        }}>
                            <Target style={{ width: '12px', height: '12px' }} /> {tx.projectMode}
                        </div>
                        <button
                            style={{
                                ...styles.button,
                                marginTop: 0,
                                backgroundColor: 'var(--aipron-info)',
                                ...(projectLoading || status?.status !== 'available' ? styles.buttonDisabled : {}),
                            }}
                            onClick={runOnProject}
                            disabled={projectLoading || status?.status !== 'available'}
                        >
                            {projectLoading ? (
                                <span className="flex items-center gap-2 justify-center font-mono tabular-nums">
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                    {tx.projectRunning}
                                    {projectProgress !== null ? ` ${projectProgress}%` : ''}
                                </span>
                            ) : (
                                <span className="flex items-center gap-2 justify-center"><PlayIcon size={16} /> {tx.runProjectBtn}</span>
                            )}
                        </button>

                        {/* Canonical to-be (FTS bridge): re-runs the ONE DACAMS formula over an
                            FTS datasheet of improved timestamps and stores the to-be CC the
                            optimizer uses (capacity_source='to_be'). Doubles the pipeline cost. */}
                        <button
                            style={{
                                ...styles.button,
                                marginTop: '10px',
                                backgroundColor: 'transparent',
                                border: '1px solid var(--border)',
                                color: 'var(--text-secondary)',
                                ...(tobeLoading ? styles.buttonDisabled : {}),
                            }}
                            onClick={computeTobe}
                            disabled={tobeLoading}
                            title="Recalcula el tri-nodo to-be por la fórmula canónica DACAMS (no un multiplicador). Tarda ~varios minutos."
                        >
                            {tobeLoading ? (
                                <span className="flex items-center gap-2 justify-center font-mono tabular-nums">
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                    Calculando To-Be{tobeProgress !== null ? ` ${tobeProgress}%` : '…'}
                                </span>
                            ) : (
                                <span className="flex items-center gap-2 justify-center">Calcular To-Be canónico</span>
                            )}
                        </button>

                        {tobeError && (
                            <div style={{
                                marginTop: '12px', padding: '10px', fontSize: '12px',
                                backgroundColor: 'color-mix(in srgb, var(--aipron-warning) 10%, transparent)',
                                border: '1px solid color-mix(in srgb, var(--aipron-warning) 30%, transparent)',
                                borderRadius: '8px', color: 'var(--aipron-warning)',
                            }}>{tobeError}</div>
                        )}

                        {tobeResult && !tobeError && (
                            <div style={{
                                marginTop: '12px', padding: '12px', fontSize: '13px',
                                backgroundColor: 'var(--surface)', border: '1px solid var(--border)',
                                borderRadius: '8px', color: 'var(--text-secondary)',
                            }}>
                                {tobeResult.available ? (
                                    <>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                            <span>CC as-is</span>
                                            <span className="font-mono tabular-nums" style={{ color: 'var(--text-primary)' }}>
                                                {tobeResult.asis_cc != null ? tobeResult.asis_cc.toFixed(2) : '—'}
                                            </span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                            <span>CC to-be (canónico)</span>
                                            <span className="font-mono tabular-nums" style={{ color: 'var(--aipron-accent)' }}>
                                                {tobeResult.tobe_cc != null ? tobeResult.tobe_cc.toFixed(2) : '—'}
                                            </span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--text-tertiary)' }}>
                                            <span>ΔRAC {tobeResult.deltas?.rac != null ? tobeResult.deltas.rac.toFixed(2) : '—'}</span>
                                            <span>ΔCC {tobeResult.deltas?.cc != null ? tobeResult.deltas.cc.toFixed(2) : '—'}</span>
                                        </div>
                                        <div style={{ marginTop: '8px', fontSize: '11px', color: 'var(--text-tertiary)', lineHeight: 1.4 }}>
                                            El CC to-be solo sube si el nodo limitante mejora (la mejora del AMS eleva la RAC vía rotación de stands; si limita pista o terminal, el CC no sube). El optimizer ya puede usar este CC (capacity_source='to_be').
                                        </div>
                                    </>
                                ) : (
                                    <span style={{ color: 'var(--text-tertiary)' }}>To-Be no disponible (sin dataset o sin techo de taxi medido).</span>
                                )}
                            </div>
                        )}

                        {/* Toggle: size the optimizer against the to-be CC envelope. */}
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px', fontSize: '12px', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={useTobeCapacity}
                                onChange={e => setUseTobeCapacity(e.target.checked)}
                            />
                            {language === 'es'
                                ? 'Optimizar contra la capacidad To-Be (capacity_source=to_be)'
                                : 'Optimize against the To-Be capacity (capacity_source=to_be)'}
                        </label>
                        {useTobeCapacity && (
                            <div style={{ marginTop: '4px', fontSize: '11px', color: 'var(--text-tertiary)', lineHeight: 1.4 }}>
                                {language === 'es'
                                    ? 'Requiere haber calculado el To-Be canónico antes; si no hay CC to-be guardado, cae al as-is (marcado).'
                                    : 'Requires a prior canonical To-Be run; with no stored to-be CC it falls back to as-is (flagged).'}
                            </div>
                        )}

                        {/* Honest empty-data state for the 409 (no flight-stand allocation) */}
                        {projectNoAssignment && projectError && (
                            <div style={{
                                marginTop: '16px',
                                padding: '12px',
                                backgroundColor: 'color-mix(in srgb, var(--aipron-warning) 10%, transparent)',
                                border: '1px solid color-mix(in srgb, var(--aipron-warning) 30%, transparent)',
                                borderRadius: '8px',
                                color: 'var(--aipron-warning)',
                                fontSize: '13px',
                            }}>
                                <div className="flex items-center gap-1" style={{ fontWeight: 600, marginBottom: '4px' }}>
                                    <WarningIcon size={16} /> {tx.noAssignmentTitle}
                                </div>
                                <div style={{ color: 'var(--text-secondary)' }}>{projectError}</div>
                            </div>
                        )}

                        {/* Generic project-run error (crash, poll failure, etc.) */}
                        {projectError && !projectNoAssignment && (
                            <div style={{
                                marginTop: '16px',
                                padding: '12px',
                                backgroundColor: 'color-mix(in srgb, var(--aipron-danger) 10%, transparent)',
                                border: '1px solid color-mix(in srgb, var(--aipron-danger) 30%, transparent)',
                                borderRadius: '8px',
                                color: 'var(--aipron-danger)',
                                fontSize: '13px',
                            }}>
                                <span className="flex items-center gap-1"><XCircle className="w-4 h-4" /> {projectError}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Results Panel */}
                <div>
                    {/* Results Summary */}
                    {result && (
                        <div style={styles.resultCard}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>
                                    <span className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5" /> {tx.completed}</span>
                                </h3>
                                <span style={{
                                    ...styles.statusBadge,
                                    backgroundColor: result.status === 'OPTIMAL' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(234, 179, 8, 0.2)',
                                    color: result.status === 'OPTIMAL' ? '#10B981' : '#EAB308',
                                }}>
                                    {result.status}
                                </span>
                            </div>

                            <div style={styles.resultGrid}>
                                <div style={styles.resultItem}>
                                    <div style={styles.resultValue}>{result.slots_created.length}</div>
                                    <div style={styles.resultLabel}>{tx.slotsCreated}</div>
                                </div>
                                <div style={styles.resultItem}>
                                    <div style={styles.resultValue}>{result.total_slack_recovered}</div>
                                    <div style={styles.resultLabel}>{tx.minRecovered}</div>
                                </div>
                                <div style={styles.resultItem}>
                                    <div style={styles.resultValue}>{result.fragmentation_reduction.toFixed(1)}%</div>
                                    <div style={styles.resultLabel}>{tx.fragReduction}</div>
                                </div>
                                <div style={styles.resultItem}>
                                    <div style={styles.resultValue}>{result.solve_time_ms}</div>
                                    <div style={styles.resultLabel}>{tx.solveTime}</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Canonical Part-5 KPIs + Tri-Node compliance + provenance —
                        only present when the result came from a real-project run. */}
                    {result?.kpis && (
                        <div style={{ ...styles.panel, marginBottom: '24px' }}>
                            <h3 style={styles.panelTitle} className="flex items-center gap-2">
                                <ScalesIcon size={20} /> {tx.canonicalKpisTitle}
                            </h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '16px' }}>
                                <div style={styles.resultItem}>
                                    <div className="font-mono tabular-nums" style={{ ...styles.resultValue, color: 'var(--aipron-accent)' }}>
                                        {(result.kpis.equity_index * 100).toFixed(1)}%
                                    </div>
                                    <div style={styles.resultLabel}>{tx.equityIndex}</div>
                                </div>
                                <div style={styles.resultItem}>
                                    <div className="font-mono tabular-nums" style={{ ...styles.resultValue, color: 'var(--aipron-accent)' }}>
                                        {(result.kpis.fragmentation_reduction * 100).toFixed(1)}%
                                    </div>
                                    <div style={styles.resultLabel}>{tx.fragReductionCanonical}</div>
                                </div>
                                <div style={styles.resultItem}>
                                    <div className="font-mono tabular-nums" style={{ ...styles.resultValue, color: 'var(--aipron-accent)' }}>
                                        {result.kpis.utilization_gain.toFixed(2)}x
                                    </div>
                                    <div style={styles.resultLabel}>{tx.utilizationGain}</div>
                                </div>
                                <div style={styles.resultItem}>
                                    <div
                                        className="font-mono tabular-nums"
                                        style={{
                                            ...styles.resultValue,
                                            color: result.kpis.violation_count > 0 ? 'var(--aipron-danger)' : 'var(--aipron-success)',
                                        }}
                                    >
                                        {result.kpis.violation_count}
                                    </div>
                                    <div style={styles.resultLabel}>{tx.violations}</div>
                                </div>
                                <div style={styles.resultItem}>
                                    <div
                                        className="flex items-center justify-center gap-1"
                                        style={{
                                            ...styles.resultValue,
                                            fontSize: '18px',
                                            color: result.kpis.node_compliance_ok ? 'var(--aipron-success)' : 'var(--aipron-danger)',
                                        }}
                                    >
                                        {result.kpis.node_compliance_ok ? <ShieldCheckIcon size={20} /> : <ShieldWarningIcon size={20} />}
                                        {result.kpis.node_compliance_ok ? tx.nodeComplianceOk : tx.nodeComplianceFail}
                                    </div>
                                    <div style={styles.resultLabel}>{tx.nodeComplianceKpi}</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Per-hour Tri-Node compliance table */}
                    {result?.node_compliance && result.node_compliance.length > 0 && (
                        <div style={{ ...styles.panel, marginBottom: '24px' }}>
                            <h3 style={styles.panelTitle} className="flex items-center gap-2">
                                <ChartLineUpIcon size={20} /> {tx.nodeComplianceTitle}
                            </h3>
                            <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '-12px', marginBottom: '16px', lineHeight: 1.5 }}>
                                {tx.nodeComplianceDesc}
                            </p>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                    <thead>
                                        <tr style={{ textAlign: 'left', color: 'var(--text-secondary)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                                            <th style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>{tx.colHour}</th>
                                            <th style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>{tx.colCapCc}</th>
                                            <th style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>{tx.colExistingMov}</th>
                                            <th style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>{tx.colHeadroom}</th>
                                            <th style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>{tx.colSlotsUsed}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {result.node_compliance.map(row => {
                                            const violated = row.slots_used > row.headroom;
                                            return (
                                                <tr
                                                    key={row.hour}
                                                    style={violated ? {
                                                        backgroundColor: 'color-mix(in srgb, var(--aipron-danger) 12%, transparent)',
                                                    } : undefined}
                                                >
                                                    <td className="font-mono tabular-nums" style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>
                                                        {row.hour.toString().padStart(2, '0')}:00
                                                    </td>
                                                    <td className="font-mono tabular-nums" style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>{row.cap_cc}</td>
                                                    <td className="font-mono tabular-nums" style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>{row.existing_mov}</td>
                                                    <td className="font-mono tabular-nums" style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>{row.headroom}</td>
                                                    <td
                                                        className="font-mono tabular-nums"
                                                        style={{
                                                            padding: '8px 12px',
                                                            borderBottom: '1px solid var(--border)',
                                                            color: violated ? 'var(--aipron-danger)' : undefined,
                                                            fontWeight: violated ? 700 : undefined,
                                                        }}
                                                    >
                                                        {row.slots_used}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Provenance (glass box) */}
                    {result?.provenance && (
                        <div style={{ ...styles.panel, marginBottom: '24px' }}>
                            <button
                                onClick={() => setProvenanceOpen(o => !o)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: 'var(--text-primary)',
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    padding: 0,
                                    width: '100%',
                                    textAlign: 'left',
                                }}
                            >
                                {provenanceOpen ? <CaretDownIcon size={16} /> : <CaretRightIcon size={16} />}
                                {tx.provenanceTitle}
                            </button>
                            {provenanceOpen && (
                                <div style={{ marginTop: '16px' }}>
                                    {Object.keys(result.provenance).length === 0 ? (
                                        <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{tx.provenanceEmpty}</p>
                                    ) : (
                                        <div style={{ overflowX: 'auto' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                                <tbody>
                                                    {Object.entries(result.provenance).map(([key, value]) => (
                                                        <tr key={key}>
                                                            <td style={{
                                                                padding: '6px 12px',
                                                                borderBottom: '1px solid var(--border)',
                                                                color: 'var(--text-secondary)',
                                                                verticalAlign: 'top',
                                                                whiteSpace: 'nowrap',
                                                            }}>
                                                                {key}
                                                            </td>
                                                            <td
                                                                className="font-mono"
                                                                style={{
                                                                    padding: '6px 12px',
                                                                    borderBottom: '1px solid var(--border)',
                                                                    color: 'var(--text-primary)',
                                                                    wordBreak: 'break-word',
                                                                }}
                                                            >
                                                                {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Empty State (no results yet) */}
                    {!result && (
                        <div style={styles.panel}>
                            {projectLoading ? (
                                <div style={styles.loadingOverlay}>
                                    <div style={styles.spinner} />
                                    <p style={{ marginTop: '16px' }}>{tx.runningMip}</p>
                                    <p style={{ fontSize: '12px' }}>{tx.consolidating}</p>
                                </div>
                            ) : (
                                <div style={styles.loadingOverlay}>
                                    <PuzzleIcon style={{ width: '48px', height: '48px', marginBottom: '16px' }} />
                                    <p style={{ fontSize: '16px', fontWeight: 500 }}>{tx.emptyTitle}</p>
                                    <p style={{ fontSize: '13px', maxWidth: '400px', textAlign: 'center', lineHeight: 1.6 }}>
                                        {tx.emptyDesc}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

/** Small inline badge: green "from DACAMS" when a config value came from the
 * project's analysis_results, gray "default" when it's still the hardcoded
 * default. Bilingual (ES/EN), following the same pattern as RunCapPage's
 * SourceBadge — never shown as "from DACAMS" unless a real key was read. */
const SourceBadge: React.FC<{ fromDacams: boolean; tx: { fromDacamsBadge: string; defaultBadge: string } }> = ({ fromDacams, tx }) => (
    fromDacams
        ? <span className="text-[9px] px-1.5 py-0.5 rounded-sm bg-emerald-500/20 text-emerald-400 font-bold tracking-wide align-middle">{tx.fromDacamsBadge}</span>
        : <span className="text-[9px] px-1.5 py-0.5 rounded-sm bg-gray-500/20 text-gray-400 font-bold tracking-wide align-middle">{tx.defaultBadge}</span>
);

export default OptimizerPage;
